import React, { useState, useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  makeStyles,
  Switch,
  FormControlLabel,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  TableContainer,
} from "@material-ui/core";
import { Alert, Autocomplete } from "@material-ui/lab";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import CloseIcon from "@material-ui/icons/Close";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import {
  getContrato,
  createContrato,
  updateContrato,
  listContratoTemplates,
  uploadContratoLogo,
} from "../../services/contratoService";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 24px",
    borderBottom: "1px solid #e0e0e0",
  },
  content: {
    padding: "16px 24px",
    overflowY: "auto",
  },
  monoArea: {
    fontFamily: "monospace",
    fontSize: 13,
    width: "100%",
    minHeight: 200,
    padding: 8,
    border: "1px solid #ccc",
    borderRadius: 4,
    resize: "vertical",
    boxSizing: "border-box",
  },
  colorRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  colorInput: {
    width: 48,
    height: 36,
    padding: 0,
    border: "none",
    cursor: "pointer",
    borderRadius: 4,
  },
  warningBanner: {
    backgroundColor: "#fff8e1",
    border: "1px solid #ffd54f",
    borderRadius: 4,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
  },
  logoPreview: { maxHeight: 80, maxWidth: 200, marginTop: 8, display: "block" },
}));

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box mt={2}>{children}</Box>;
}

const EMPTY_FORM = {
  title: "",
  contactId: null,
  templateId: null,
  dueDate: "",
  requireVerificationCode: false,
  primaryColor: "#1e40af",
  headerColor: "#1e293b",
  logoUrl: "",
  cssStyle: "",
  htmlContent: "",
};

export default function ContratoModal({ open, onClose, contratoId, templateId: initialTemplateId }) {
  const classes = useStyles();
  const isNew = !contratoId;

  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [contact, setContact] = useState(null);
  const [contactSearch, setContactSearch] = useState("");
  const [contactOptions, setContactOptions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [signers, setSigners] = useState([]);
  const [clauses, setClauses] = useState([]);
  const [items, setItems] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [serviceSearch, setServiceSearch] = useState("");
  const [serviceOptions, setServiceOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [status, setStatus] = useState("draft");

  // Garante que o spinner aparece ANTES da primeira pintura ao abrir em modo edição
  useLayoutEffect(() => {
    if (open && !isNew) setLoading(true);
    if (!open) setLoading(false);
  }, [open, contratoId]); // eslint-disable-line

  // Resetar ao abrir
  useEffect(() => {
    if (!open) return;
    setTab(0);
    setSnack({ open: false, message: "", severity: "success" });

    listContratoTemplates()
      .then((data) => setTemplates(Array.isArray(data) ? data : []))
      .catch(() => {});

    if (!isNew) {
      setLoading(true);
      getContrato(contratoId)
        .then((c) => {
          setForm({
            title: c.title || "",
            contactId: c.contactId || null,
            templateId: c.templateId || null,
            dueDate: c.dueDate || "",
            requireVerificationCode: c.requireVerificationCode || false,
            primaryColor: c.primaryColor || "#1e40af",
            headerColor: c.headerColor || "#1e293b",
            logoUrl: c.logoUrl || "",
            cssStyle: c.cssStyle || "",
            htmlContent: c.htmlContent || "",
          });
          setStatus(c.status || "draft");
          setSigners(Array.isArray(c.signers) ? c.signers : []);
          setClauses(Array.isArray(c.clauses) ? c.clauses : []);
          setItems(Array.isArray(c.items) ? c.items : []);
          if (c.contact) setContact(c.contact);
        })
        .catch(() => setSnack({ open: true, message: "Erro ao carregar contrato.", severity: "error" }))
        .finally(() => setLoading(false));
    } else {
      setForm({
        ...EMPTY_FORM,
        templateId: initialTemplateId || null,
      });
      setStatus("draft");
      setSigners([]);
      setClauses([]);
      setItems([]);
      setContact(null);
      setContactSearch("");
    }
  }, [open, contratoId, isNew, initialTemplateId]);

  // Busca de contatos
  useEffect(() => {
    if (contactSearch.length < 2) { setContactOptions([]); return; }
    const t = setTimeout(() => {
      api.get("/contacts", { params: { searchParam: contactSearch, pageNumber: 1 } })
        .then((r) => setContactOptions(r.data.contacts || []))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [contactSearch]);

  // Busca de produtos
  useEffect(() => {
    if (productSearch.length < 2) { setProductOptions([]); return; }
    const t = setTimeout(() => {
      api.get("/produtos", { params: { nome: productSearch } })
        .then((r) => setProductOptions(r.data.products || r.data.data || r.data || []))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [productSearch]);

  // Busca de serviços
  useEffect(() => {
    if (serviceSearch.length < 2) { setServiceOptions([]); return; }
    const t = setTimeout(() => {
      api.get("/servicos", { params: { search: serviceSearch } })
        .then((r) => setServiceOptions(r.data.servicos || r.data || []))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [serviceSearch]);

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.title.trim()) {
      setSnack({ open: true, message: "O título é obrigatório.", severity: "warning" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        contactId: contact ? contact.id : null,
        signers,
        clauses,
        items,
      };
      if (isNew) {
        await createContrato(payload);
        setSnack({ open: true, message: "Contrato criado!", severity: "success" });
        setTimeout(() => onClose(true), 800);
      } else {
        await updateContrato(contratoId, payload);
        setSnack({ open: true, message: "Contrato salvo!", severity: "success" });
        setTimeout(() => onClose(true), 800);
      }
    } catch (e) {
      setSnack({
        open: true,
        message: e?.response?.data?.error || "Erro ao salvar.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const addSigner = () => {
    setSigners((prev) => [
      ...prev,
      { id: String(Date.now()), name: "", email: "", phone: "", role: "", signed: false },
    ]);
  };
  const updateSigner = (idx, field, value) => {
    setSigners((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };
  const removeSigner = (idx) => setSigners((prev) => prev.filter((_, i) => i !== idx));

  const addClause = () => {
    setClauses((prev) => [
      ...prev,
      { id: String(Date.now()), title: `Cláusula ${prev.length + 1}`, content: "" },
    ]);
  };
  const updateClause = (idx, field, value) => {
    setClauses((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };
  const removeClause = (idx) => setClauses((prev) => prev.filter((_, i) => i !== idx));
  const moveClause = (idx, dir) => {
    setClauses((prev) => {
      const arr = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return prev;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  };

  const addProduct = (product) => {
    setItems((prev) => [
      ...prev,
      {
        id: String(Date.now()),
        type: "produto",
        refId: product.id,
        nome: product.nome,
        descricao: product.descricao || "",
        valor: product.valor || 0,
        quantidade: 1,
      },
    ]);
    setProductSearch("");
    setProductOptions([]);
  };
  const addService = (service) => {
    setItems((prev) => [
      ...prev,
      {
        id: String(Date.now()),
        type: "servico",
        refId: service.id,
        nome: service.nome,
        descricao: service.descricao || "",
        valor: service.valorOriginal || 0,
        quantidade: 1,
      },
    ]);
    setServiceSearch("");
    setServiceOptions([]);
  };
  const updateItem = (idx, field, value) => {
    setItems((prev) => prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item)));
  };
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const totalItems = items.reduce((acc, it) => acc + (Number(it.valor) * Number(it.quantidade || 1)), 0);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const url = await uploadContratoLogo(file);
      setField("logoUrl", url);
    } catch {
      setSnack({ open: true, message: "Erro ao fazer upload do logo.", severity: "error" });
    }
  };

  const isReadOnly = status !== "draft";

  return (
    <>
      <Dialog open={open} onClose={() => onClose(false)} maxWidth="lg" fullWidth>
        <Box className={classes.dialogTitle}>
          <Typography variant="h6">
            {isNew ? "Novo Contrato" : "Editar Contrato"}
          </Typography>
          <IconButton size="small" onClick={() => onClose(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent className={classes.content}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {isReadOnly && (
                <Box className={classes.warningBanner}>
                  <Typography variant="body2">
                    Este contrato já foi enviado. Algumas alterações podem não ser reenviadas automaticamente.
                  </Typography>
                </Box>
              )}

              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="Geral" />
                <Tab label="Itens" />
                <Tab label="Cláusulas" />
                <Tab label="Signatários" />
                <Tab label="Layout" />
              </Tabs>

              {/* TAB GERAL */}
              <TabPanel value={tab} index={0}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Título do Contrato"
                      fullWidth
                      required
                      value={form.title}
                      onChange={(e) => { const v = e.target.value; setField("title", v); }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={contactOptions}
                      getOptionLabel={(o) => `${o.name} — ${o.number || o.email || ""}`}
                      value={contact}
                      onInputChange={(_, v) => setContactSearch(v)}
                      onChange={(_, v) => { setContact(v); setField("contactId", v ? v.id : null); }}
                      renderInput={(params) => (
                        <TextField {...params} label="Cliente (contato)" fullWidth />
                      )}
                      noOptionsText="Digite para buscar..."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Modelo de Contrato</InputLabel>
                      <Select
                        value={form.templateId || ""}
                        onChange={(e) => { const v = e.target.value; setField("templateId", v || null); }}
                      >
                        <MenuItem value="">Nenhum</MenuItem>
                        {templates.map((t) => (
                          <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Data de Vencimento"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={form.dueDate || ""}
                      onChange={(e) => { const v = e.target.value; setField("dueDate", v); }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.requireVerificationCode}
                          onChange={(e) => { const v = e.target.checked; setField("requireVerificationCode", v); }}
                          color="primary"
                        />
                      }
                      label="Exigir código de verificação na assinatura"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Conteúdo HTML do Contrato
                    </Typography>
                    <textarea
                      className={classes.monoArea}
                      value={form.htmlContent || ""}
                      onChange={(e) => { const v = e.target.value; setField("htmlContent", v); }}
                      placeholder="<p>Texto livre do contrato...</p>"
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              {/* TAB ITENS */}
              <TabPanel value={tab} index={1}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={productOptions}
                      getOptionLabel={(o) => `${o.nome} — R$ ${Number(o.valor || 0).toFixed(2)}`}
                      inputValue={productSearch}
                      value={null}
                      onInputChange={(_, v) => setProductSearch(v)}
                      onChange={(_, v) => { if (v) addProduct(v); }}
                      renderInput={(params) => (
                        <TextField {...params} label="Buscar produto" placeholder="Digite o nome..." />
                      )}
                      noOptionsText="Nenhum produto encontrado"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={serviceOptions}
                      getOptionLabel={(o) => `${o.nome} — R$ ${Number(o.valorOriginal || 0).toFixed(2)}`}
                      inputValue={serviceSearch}
                      value={null}
                      onInputChange={(_, v) => setServiceSearch(v)}
                      onChange={(_, v) => { if (v) addService(v); }}
                      renderInput={(params) => (
                        <TextField {...params} label="Buscar serviço" placeholder="Digite o nome..." />
                      )}
                      noOptionsText="Nenhum serviço encontrado"
                    />
                  </Grid>
                </Grid>
                <Box mt={2}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Nome</TableCell>
                          <TableCell>Valor Unit.</TableCell>
                          <TableCell>Qtd</TableCell>
                          <TableCell>Total</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item, idx) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.type === "produto" ? "Produto" : "Serviço"}</TableCell>
                            <TableCell>{item.nome}</TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={item.valor}
                                onChange={(e) => { const v = e.target.value; updateItem(idx, "valor", v); }}
                                style={{ width: 90 }}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={item.quantidade}
                                onChange={(e) => { const v = e.target.value; updateItem(idx, "quantidade", v); }}
                                style={{ width: 60 }}
                                inputProps={{ min: 1 }}
                              />
                            </TableCell>
                            <TableCell>
                              R$ {(Number(item.valor) * Number(item.quantidade || 1)).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => removeItem(idx)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        {items.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} align="center" style={{ color: "#888" }}>
                              Nenhum item adicionado.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {items.length > 0 && (
                    <Box mt={1} textAlign="right">
                      <Typography variant="subtitle1">
                        <strong>Total: R$ {totalItems.toFixed(2)}</strong>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </TabPanel>

              {/* TAB CLÁUSULAS */}
              <TabPanel value={tab} index={2}>
                <Button startIcon={<AddIcon />} onClick={addClause} variant="outlined" size="small">
                  Adicionar Cláusula
                </Button>
                <Box mt={2}>
                  {clauses.map((cl, idx) => (
                    <Paper key={cl.id} variant="outlined" style={{ padding: 12, marginBottom: 12 }}>
                      <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                        <TextField
                          size="small"
                          label="Título da cláusula"
                          value={cl.title}
                          onChange={(e) => { const v = e.target.value; updateClause(idx, "title", v); }}
                          style={{ flexGrow: 1 }}
                        />
                        <IconButton size="small" onClick={() => moveClause(idx, -1)} disabled={idx === 0}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => moveClause(idx, 1)} disabled={idx === clauses.length - 1}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => removeClause(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <textarea
                        className={classes.monoArea}
                        style={{ minHeight: 120, fontFamily: "inherit", fontSize: 14 }}
                        value={cl.content}
                        onChange={(e) => { const v = e.target.value; updateClause(idx, "content", v); }}
                        placeholder="Conteúdo da cláusula..."
                      />
                    </Paper>
                  ))}
                  {clauses.length === 0 && (
                    <Typography color="textSecondary" variant="body2">
                      Nenhuma cláusula adicionada.
                    </Typography>
                  )}
                </Box>
              </TabPanel>

              {/* TAB SIGNATÁRIOS */}
              <TabPanel value={tab} index={3}>
                <Button startIcon={<AddIcon />} onClick={addSigner} variant="outlined" size="small">
                  Adicionar Signatário
                </Button>
                <Box mt={2}>
                  {signers.map((s, idx) => (
                    <Paper key={s.id} variant="outlined" style={{ padding: 12, marginBottom: 12 }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} md={3}>
                          <TextField
                            size="small"
                            label="Nome"
                            fullWidth
                            value={s.name}
                            onChange={(e) => { const v = e.target.value; updateSigner(idx, "name", v); }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            size="small"
                            label="E-mail"
                            fullWidth
                            value={s.email}
                            onChange={(e) => { const v = e.target.value; updateSigner(idx, "email", v); }}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            size="small"
                            label="Telefone/WhatsApp"
                            fullWidth
                            value={s.phone}
                            onChange={(e) => { const v = e.target.value; updateSigner(idx, "phone", v); }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            size="small"
                            label="Cargo/Papel"
                            fullWidth
                            value={s.role}
                            onChange={(e) => { const v = e.target.value; updateSigner(idx, "role", v); }}
                            placeholder="Ex: Contratante"
                          />
                        </Grid>
                        <Grid item xs={12} md={1}>
                          <IconButton size="small" onClick={() => removeSigner(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                  {signers.length === 0 && (
                    <Typography color="textSecondary" variant="body2">
                      Nenhum signatário adicional. O cliente vinculado será o signatário principal.
                    </Typography>
                  )}
                </Box>
              </TabPanel>

              {/* TAB LAYOUT */}
              <TabPanel value={tab} index={4}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box className={classes.colorRow}>
                      <input
                        type="color"
                        className={classes.colorInput}
                        value={form.primaryColor}
                        onChange={(e) => { const v = e.target.value; setField("primaryColor", v); }}
                      />
                      <TextField
                        size="small"
                        label="Cor Primária"
                        value={form.primaryColor}
                        onChange={(e) => { const v = e.target.value; setField("primaryColor", v); }}
                        style={{ width: 160 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box className={classes.colorRow}>
                      <input
                        type="color"
                        className={classes.colorInput}
                        value={form.headerColor}
                        onChange={(e) => { const v = e.target.value; setField("headerColor", v); }}
                      />
                      <TextField
                        size="small"
                        label="Cor do Cabeçalho"
                        value={form.headerColor}
                        onChange={(e) => { const v = e.target.value; setField("headerColor", v); }}
                        style={{ width: 160 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Logo</Typography>
                    <input type="file" accept="image/*" onChange={handleLogoUpload} />
                    {form.logoUrl && (
                      <img
                        src={form.logoUrl.startsWith("/public") ? getBackendUrl() + form.logoUrl : form.logoUrl}
                        alt="Logo"
                        className={classes.logoPreview}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>CSS Personalizado</Typography>
                    <textarea
                      className={classes.monoArea}
                      value={form.cssStyle || ""}
                      onChange={(e) => { const v = e.target.value; setField("cssStyle", v); }}
                      placeholder="/* CSS personalizado do contrato */"
                    />
                  </Grid>
                </Grid>
              </TabPanel>
            </>
          )}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={() => onClose(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
